import { useState, useEffect, useMemo } from 'react';
import { Typography, Input, Row, Col, Tooltip, Space, Button } from 'antd';
import {
  WarningOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import {
  CopyButton,
  CustomNotification,
  Form,
  FormButton,
  StyledModal,
  UploadFile,
} from 'common/components';

import { OverlayInputWrapper } from 'common/components/overlay';

import {
  _2D_BARCODE_IMAGE_ID,
  BARCODE_FORMAT,
  BARCODE_IMAGE_ID,
  PRODUCT_IMAGERY_LABELS_BY_LEVEL,
} from 'common/components/product-add/components/constants';

import { useReadBarcodeImage } from 'common/components/product-add/components/hooks/useUploadProductImage';
import { SUPPORT_IMAGES_TYPES } from 'static/Constants';
import {
  extractValueFromBarcode,
  getAutoPopulateFields,
  setBarcodeAutoPopulateWarnings,
  sortBarcodesByFormat,
} from 'common/components/product-add/components/utils/barcode';
import {
  checkActiveStatus,
  checkRequiredPrimaryFieldForm,
  isUnitPackageLevel,
  isValidCheckDigit,
  padWithZeros,
  validateGTIN,
  validateUPC12,
  validateUPC13,
  validateUPCE,
} from 'common/components/product-add/components/utils/validation';

import { useCheckIsProductOverlay } from 'hooks';

import './GtinFieldset.less';

const { Text } = Typography;

export const GtinFieldset = (props) => {
  const {
    productImagery,
    getOverlayProps,
    upcGtinError,
    upcGtinErrorMessage,
    isFieldEditable,
    prefixGtin,
  } = props;

  const [barcodeData, setBarcodeData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [mismatchData, setMismatchData] = useState(null);
  const [validatingField, setValidatingField] = useState(null); // 'gtin', 'upc8', 'upc12', or null

  const formInstance = Form.useFormInstance();
  const packageLevel = Form.useWatch('packageLevel', formInstance) || 'Unit';
  const gtin = Form.useWatch('gtin', formInstance);
  const upc13 = Form.useWatch('upc13', formInstance);
  const upc12 = Form.useWatch('upc12', formInstance);
  const upc8 = Form.useWatch('upc8', formInstance);

  const isPluItem = Form.useWatch('isPluItem', formInstance);
  const pluValue = Form.useWatch('plu', formInstance);

  const isByPassGtinAndUpc = Boolean(isPluItem);

  const isOverlay = useCheckIsProductOverlay();

  const skipBarcodeValidation = isOverlay;

  useEffect(() => {
    if (isOverlay) return;

    const fieldsToCheck = [
      { name: 'gtin', value: gtin },
      { name: 'upc12', value: upc12 },
      { name: 'upc8', value: upc8 },
      { name: 'upc13', value: upc13 },
    ];

    fieldsToCheck.forEach(({ name }) => {
      // Use getFieldValue instead of the watched value to avoid a one-render
      // lag where useWatch returns undefined while the form store already has a value
      // (e.g. when the field is pre-populated via setFieldsValue on modal open).
      const actualValue = formInstance.getFieldValue(name);
      if (!actualValue) {
        // Completely reset the field's validation state
        formInstance.setFields([
          {
            name: [name],
            errors: [],
            warnings: [],
            validating: false,
            touched: false,
            value: undefined,
          },
        ]);
        formInstance.validateFields([name]).catch(() => {
          // Ignore validation errors, we just want to reset the state
        });
      }
    });

    // Show/clear prefix warning on the gtin field
    if (prefixGtin) {
      const currentGtin = formInstance.getFieldValue('gtin');
      if (currentGtin === prefixGtin) {
        formInstance.setFields([
          {
            name: 'gtin',
            warnings: [
              'This GTIN has been auto-assigned from your GS1 Company Prefix. Please verify it is correct before submitting.',
            ],
          },
        ]);
      } else {
        formInstance.setFields([{ name: 'gtin', warnings: [] }]);
      }
    }
  }, [gtin, upc12, upc8, upc13, formInstance, isOverlay, prefixGtin]);

  const l3Barcodes =
    sortBarcodesByFormat(productImagery?.l3ImageId?.barcodes) || [];
  const l6Barcodes =
    sortBarcodesByFormat(productImagery?.l6ImageId?.barcodes) || [];

  const imageryBarcodes = [
    ...l3Barcodes.map((barcode) => ({
      ...barcode,
      label:
        PRODUCT_IMAGERY_LABELS_BY_LEVEL?.[`${packageLevel}`][BARCODE_IMAGE_ID],
    })),
    ...l6Barcodes.map((barcode) => ({
      ...barcode,
      label:
        PRODUCT_IMAGERY_LABELS_BY_LEVEL?.[`${packageLevel}`][
          _2D_BARCODE_IMAGE_ID
        ],
    })),
  ];

  const handleBarcodeFileUploaded = (data) => {
    setBarcodeData(data);
  };

  const onChangeImage = () => {
    setShowUploadModal(true);
  };

  const checkForMismatch = (detectedBarcodes) => {
    const currentFormValues = formInstance.getFieldsValue();
    const { gtin, upc8, upc12, upc13 } =
      extractValueFromBarcode(detectedBarcodes);
    const mismatches = [];

    if (currentFormValues.gtin && gtin && currentFormValues.gtin !== gtin) {
      mismatches.push({
        field: 'GTIN',
        entered: currentFormValues.gtin,
        detected: gtin,
      });
    }
    if (currentFormValues.upc8 && upc8 && currentFormValues.upc8 !== upc8) {
      mismatches.push({
        field: 'UPC8',
        entered: currentFormValues.upc8,
        detected: upc8,
      });
    }
    if (currentFormValues.upc12 && upc12 && currentFormValues.upc12 !== upc12) {
      mismatches.push({
        field: 'UPC12',
        entered: currentFormValues.upc12,
        detected: upc12,
      });
    }
    if (currentFormValues.upc13 && upc13 && currentFormValues.upc13 !== upc13) {
      mismatches.push({
        field: 'UPC13',
        entered: currentFormValues.upc13,
        detected: upc13,
      });
    }

    return mismatches;
  };

  const handleBarcodeSuccess = (barcodes) => {
    const mismatches = checkForMismatch(barcodes);

    if (mismatches.length > 0) {
      setMismatchData({ barcodes, mismatches });
      setShowMismatchModal(true);
    } else {
      const currentFormValues = formInstance.getFieldsValue();
      const { gtin, upc8, upc12, upc13 } = extractValueFromBarcode(barcodes);

      formInstance.setFieldsValue({
        ...currentFormValues,
        gtin: currentFormValues?.gtin || gtin,
        upc8: currentFormValues?.upc8 || upc8,
        upc12: currentFormValues?.upc12 || upc12,
        upc13: currentFormValues?.upc13 || upc13,
      });

      handleBarcodeFileUploaded(barcodes);
    }
  };

  const handleOverwriteWithDetected = () => {
    if (mismatchData) {
      const { gtin, upc8, upc12, upc13 } = extractValueFromBarcode(
        mismatchData.barcodes
      );

      formInstance.setFieldsValue({
        gtin: gtin || formInstance.getFieldValue('gtin'),
        upc8: upc8 || formInstance.getFieldValue('upc8'),
        upc12: upc12 || formInstance.getFieldValue('upc12'),
        upc13: upc13 || formInstance.getFieldValue('upc13'),
      });

      handleBarcodeFileUploaded(mismatchData.barcodes);
      setShowMismatchModal(false);
      setMismatchData(null);
    }
  };

  const handleValidateDigit = async (type) => {
    const value = formInstance.getFieldValue(type);

    // Validate based on type
    let isValid = false;

    if (isPluItem) {
      isValid = true;
    } else {
      if (type === 'upc8') {
        isValid = validateUPCE(value)?.isValid;
      } else if (type === 'upc12') {
        isValid = validateUPC12(value)?.isValid;
      } else if (type === 'upc13') {
        isValid = validateUPC13(value)?.isValid;
      } else if (type === 'gtin') {
        isValid = validateGTIN(value)?.isValid;
      } else {
        isValid = isValidCheckDigit(value);
      }
    }

    if (!isValid) return;

    setValidatingField(type);

    try {
      // Clear warnings on the current field
      formInstance.setFields([{ name: [type], warnings: [] }]);

      // Get current values
      const gtin = formInstance.getFieldValue('gtin');
      const upc8 = formInstance.getFieldValue('upc8');
      const upc12 = formInstance.getFieldValue('upc12');
      const upc13 = formInstance.getFieldValue('upc13');
      const currentValues = { gtin, upc13, upc12, upc8 };

      // Use the type user is validating as the source, not getAutoPopulateSource
      const fieldsToPopulate = getAutoPopulateFields(
        type,
        value,
        currentValues
      );

      if (Object.keys(fieldsToPopulate).length > 0) {
        formInstance.setFieldsValue(fieldsToPopulate);
        await setBarcodeAutoPopulateWarnings(
          formInstance,
          fieldsToPopulate,
          type
        );
      }
    } finally {
      setValidatingField(null);
    }
  };

  const handlePopulateFromPlu = () => {
    if (isPluItem && pluValue) {
      const paddedUpc12 = padWithZeros(pluValue, 12);
      const paddedGtin = padWithZeros(pluValue, 14);

      formInstance.setFieldsValue({
        gtin: gtin || paddedGtin,
        upc12: upc12 || paddedUpc12,
      });
    } else {
      // Trigger validation to clear any errors
      formInstance.validateFields(['gtin', 'upc12', 'upc8', 'upc13']);
    }
  };

  const isUnit = isUnitPackageLevel(
    Form.useWatch('packageLevel', formInstance)
  );

  const isActiveStatus = checkActiveStatus(
    Form.useWatch('status', formInstance)
  );

  const isRequired = checkRequiredPrimaryFieldForm({
    isActiveStatus,
    isUnit,
  });

  const hasImageryBarcodes = imageryBarcodes.length > 0;

  const sortedBarcodes = useMemo(
    () => sortBarcodesByFormat(barcodeData ?? []),
    [barcodeData]
  );

  const normalizeNumericWithMaxLength = (value, maxLength) => {
    if (!value) return value;
    return value
      .replace(/\D/g, '') // digits only
      .slice(0, maxLength); // enforce length HERE
  };

  const showFeedback = !isOverlay && !isByPassGtinAndUpc;

  return (
    <>
      <UploadBarcode
        form={formInstance}
        onFileUploaded={handleBarcodeFileUploaded}
        barcodeData={sortedBarcodes}
        onChangeImage={onChangeImage}
        disabled={isOverlay}
      />
      {hasImageryBarcodes && (
        <ImageryBarcodeComparison
          imageryBarcodes={imageryBarcodes}
          form={formInstance}
        />
      )}
      <FormItemDigitButton
        isOverlay={isOverlay}
        onClick={() => handleValidateDigit('gtin')}
        loading={validatingField === 'gtin'}
      >
        <Form.Item
          style={{ marginRight: 12 }}
          label='GTIN'
          name='gtin'
          hasFeedback={showFeedback}
          preserve={true}
          validateTrigger={['onBlur', 'onChange']}
          rules={
            skipBarcodeValidation
              ? []
              : isRequired
                ? [
                    {
                      required: true,
                      message: 'GTIN is required',
                    },
                    {
                      validator: (_, value) => {
                        if (!value || isByPassGtinAndUpc)
                          return Promise.resolve(undefined);
                        const validation = validateGTIN(value);
                        if (!validation.isValid) {
                          return Promise.reject(new Error(validation.error));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
                : [
                    {
                      validator: (_, value) => {
                        if (!value || isByPassGtinAndUpc)
                          return Promise.resolve();
                        const validation = validateGTIN(value);
                        if (!validation.isValid) {
                          return Promise.reject(new Error(validation.error));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
          }
          normalize={(value) => normalizeNumericWithMaxLength(value, 14)}
        >
          <OverlayInputWrapper {...getOverlayProps('gtin')}>
            <Input
              disabled={isFieldEditable('gtin')}
              onBlur={() => handleValidateDigit('gtin')}
              inputMode='numeric'
              pattern='[0-9]*'
            />
          </OverlayInputWrapper>
        </Form.Item>
      </FormItemDigitButton>
      <FormItemDigitButton
        isOverlay={isOverlay}
        onClick={() => handleValidateDigit('upc8')}
        loading={validatingField === 'upc8'}
      >
        <Form.Item
          label='UPC8'
          name='upc8'
          style={{ marginRight: 12 }}
          hasFeedback={showFeedback}
          preserve={true}
          validateTrigger={['onBlur', 'onChange']}
          rules={[
            skipBarcodeValidation
              ? []
              : {
                  validator: (_, value) => {
                    if (!value || isByPassGtinAndUpc) {
                      return Promise.resolve(undefined);
                    }
                    const validation = validateUPCE(value);
                    if (!validation.isValid) {
                      return Promise.reject(new Error(validation.error));
                    }
                    return Promise.resolve();
                  },
                },
          ]}
          normalize={(value) => normalizeNumericWithMaxLength(value, 8)}
        >
          <OverlayInputWrapper {...getOverlayProps('upc8')}>
            <Input
              disabled={isFieldEditable('upc8')}
              onBlur={() => handleValidateDigit('upc8')}
              inputMode='numeric'
              pattern='[0-9]*'
            />
          </OverlayInputWrapper>
        </Form.Item>
      </FormItemDigitButton>
      <Form.Item
        label='UPC10'
        name='upc10'
        style={{ marginRight: 12 }}
        normalize={(value) => normalizeNumericWithMaxLength(value, 10)}
      >
        <OverlayInputWrapper {...getOverlayProps('upc10')}>
          <Input
            disabled={isFieldEditable('upc10')}
            inputMode='numeric'
            pattern='[0-9]*'
          />
        </OverlayInputWrapper>
      </Form.Item>
      <Form.Item
        label='UPC11'
        name='upc11'
        style={{ marginRight: 12 }}
        normalize={(value) => normalizeNumericWithMaxLength(value, 11)}
      >
        <OverlayInputWrapper {...getOverlayProps('upc11')}>
          <Input
            disabled={isFieldEditable('upc11')}
            inputMode='numeric'
            pattern='[0-9]*'
          />
        </OverlayInputWrapper>
      </Form.Item>
      <FormItemDigitButton
        isOverlay={isOverlay}
        onClick={() => handleValidateDigit('upc12')}
        loading={validatingField === 'upc12'}
      >
        <Form.Item
          style={{ marginRight: 12 }}
          label='UPC12'
          name='upc12'
          hasFeedback={showFeedback}
          preserve={true}
          validateTrigger={['onBlur', 'onChange']}
          rules={
            skipBarcodeValidation
              ? []
              : isRequired
                ? [
                    {
                      validator: (_, value) => {
                        if (!value || isByPassGtinAndUpc) {
                          return Promise.resolve();
                        }
                        const validation = validateUPC12(value);
                        if (!validation.isValid) {
                          return Promise.reject(new Error(validation.error));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
                : [
                    {
                      validator: (_, value) => {
                        if (!value || isByPassGtinAndUpc) {
                          return Promise.resolve();
                        }
                        const validation = validateUPC12(value);
                        if (!validation.isValid) {
                          return Promise.reject(new Error(validation.error));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
          }
          normalize={(value) => normalizeNumericWithMaxLength(value, 12)}
        >
          <OverlayInputWrapper {...getOverlayProps('upc12')}>
            <Input
              disabled={isFieldEditable('upc12')}
              onBlur={() => handleValidateDigit('upc12')}
              inputMode='numeric'
              pattern='[0-9]*'
            />
          </OverlayInputWrapper>
        </Form.Item>
      </FormItemDigitButton>
      <FormItemDigitButton
        isOverlay={isOverlay}
        onClick={() => handleValidateDigit('upc13')}
        loading={validatingField === 'upc13'}
      >
        <Form.Item
          label='UPC13'
          name='upc13'
          style={{ marginRight: 12 }}
          hasFeedback={showFeedback}
          preserve={true}
          validateTrigger={['onBlur', 'onChange']}
          normalize={(value) => normalizeNumericWithMaxLength(value, 13)}
          rules={
            skipBarcodeValidation
              ? []
              : [
                  {
                    validator: (_, value) => {
                      if (!value || isByPassGtinAndUpc) {
                        return Promise.resolve(undefined);
                      }
                      const validation = validateUPC13(value);
                      if (!validation.isValid) {
                        return Promise.reject(new Error(validation.error));
                      }
                      return Promise.resolve();
                    },
                  },
                ]
          }
        >
          <OverlayInputWrapper {...getOverlayProps('upc13')}>
            <Input
              disabled={isFieldEditable('upc13')}
              onBlur={() => handleValidateDigit('upc13')}
              inputMode='numeric'
              pattern='[0-9]*'
            />
          </OverlayInputWrapper>
        </Form.Item>
      </FormItemDigitButton>

      <Form.Item label='PLU' name='plu' style={{ marginRight: 12 }}>
        <OverlayInputWrapper {...getOverlayProps('plu')}>
          <Input
            disabled={!isPluItem || isFieldEditable('plu')}
            inputMode='numeric'
            pattern='[0-9]*'
            onBlur={handlePopulateFromPlu}
          />
        </OverlayInputWrapper>
      </Form.Item>

      {upcGtinError ? <Text type='danger'>{upcGtinErrorMessage}</Text> : null}
      {showUploadModal && (
        <UploadBarcodeModal
          form={formInstance}
          visible={showUploadModal}
          onCancel={() => setShowUploadModal(false)}
          onFileUploaded={(data) => {
            handleBarcodeSuccess(data);
            setShowUploadModal(false);
          }}
        />
      )}
      {showMismatchModal && mismatchData && (
        <MismatchWarningModal
          visible={showMismatchModal}
          mismatches={mismatchData.mismatches}
          onOverwrite={handleOverwriteWithDetected}
          onCancel={() => {
            setShowMismatchModal(false);
            setMismatchData(null);
          }}
        />
      )}
    </>
  );
};

const ValidateButton = (props) => {
  return (
    <Tooltip title='Validate'>
      <Button type='text' size='small' icon={<ReloadOutlined />} {...props} />
    </Tooltip>
  );
};

const FormItemDigitButton = ({ children, isOverlay, onClick, loading }) => {
  return (
    <div style={{ position: 'relative' }} className='warning-as-error'>
      {children}
      {!isOverlay && (
        <div style={{ position: 'absolute', right: 2, top: 3 }}>
          <ValidateButton
            onClick={onClick}
            disabled={loading}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

const UploadBarcode = ({ barcodeData, onChangeImage, disabled, children }) => {
  const hasBarcodes = barcodeData && barcodeData.length > 0;

  return (
    <>
      <div style={{ paddingBottom: 16 }}>
        {barcodeData ? (
          <div
            style={{
              position: 'relative',
              borderRadius: 4,
            }}
          >
            <Tooltip title='Change barcode'>
              <EditOutlined
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  fontSize: 14,
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  zIndex: 1,
                }}
                onClick={onChangeImage}
              />
            </Tooltip>

            {hasBarcodes && (
              <div>
                <BarcodeDetectionStatus barcodes={barcodeData} />
              </div>
            )}
            {children}
          </div>
        ) : (
          <Typography.Text strong style={{ fontSize: 14 }}>
            <Typography.Text>
              Please enter your product identifiers below,
            </Typography.Text>{' '}
            or click
            <Typography.Link
              strong
              underline
              style={{ padding: 6, fontSize: 14 }}
              onClick={onChangeImage}
              disabled={disabled}
            >
              here
            </Typography.Link>
            to load a photo of your barcode
          </Typography.Text>
        )}
      </div>
    </>
  );
};

const ImageryBarcodeComparison = ({ imageryBarcodes, form }) => {
  const [groupedResults, setGroupedResults] = useState({});

  useEffect(() => {
    const currentFormValues = form.getFieldsValue();

    const grouped = {};

    imageryBarcodes.forEach((barcode) => {
      const label = barcode.label || 'Unknown';

      if (!grouped[label]) {
        grouped[label] = [];
      }

      const extracted = extractValueFromBarcode([barcode]);

      if (extracted.gtin) {
        const match = currentFormValues.gtin === extracted.gtin;
        grouped[label].push({
          field: 'GTIN',
          detected: extracted.gtin,
          entered: currentFormValues.gtin || null,
          match,
        });
      }

      if (extracted.upc8) {
        const match = currentFormValues.upc8 === extracted.upc8;
        grouped[label].push({
          field: 'UPC8',
          detected: extracted.upc8,
          entered: currentFormValues.upc8 || null,
          match,
        });
      }

      if (extracted.upc12) {
        const match = currentFormValues.upc12 === extracted.upc12;
        grouped[label].push({
          field: 'UPC12',
          detected: extracted.upc12,
          entered: currentFormValues.upc12 || null,
          match,
        });
      }

      if (extracted.upc13) {
        const match = currentFormValues.upc13 === extracted.upc13;
        grouped[label].push({
          field: 'UPC13',
          detected: extracted.upc13,
          entered: currentFormValues.upc13 || null,
          match,
        });
      }
    });

    setGroupedResults(grouped);
  }, [imageryBarcodes, form]);

  if (Object.keys(groupedResults).length === 0) {
    return null;
  }

  const allResults = Object.values(groupedResults).flat();
  const hasMismatches = allResults.some(
    (result) => result.entered && !result.match
  );

  return (
    <div style={{ marginBottom: 12, backgroundColor: '#fafafa', padding: 8 }}>
      <div style={{ marginBottom: hasMismatches ? 12 : 0 }}>
        {Object.keys(groupedResults).map((label) => (
          <div key={label} style={{ marginBottom: 8 }}>
            <Typography.Text
              strong
              style={{
                display: 'block',
                marginBottom: 4,
              }}
            >
              {label}:
            </Typography.Text>
            {groupedResults[label].map((result, index) => (
              <Row
                key={index}
                style={{ paddingLeft: 16, marginBottom: 2 }}
                align='middle'
              >
                <Col flex='50px'>
                  <Typography.Text style={{ color: '#595959' }}>
                    {result.field}:
                  </Typography.Text>
                </Col>
                <Col flex='120px'>
                  <Typography.Text style={{ color: '#595959' }} strong>
                    <code
                      style={{
                        fontSize: 14,
                        color:
                          result.entered && result.match
                            ? '#73d13d'
                            : '#ff4d4f',
                      }}
                    >
                      {result.detected}
                    </code>
                  </Typography.Text>
                </Col>

                <Col flex='auto'>
                  <CopyButton text={result.detected} />
                </Col>
              </Row>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const MismatchWarningModal = ({
  visible,
  mismatches,
  onOverwrite,
  onCancel,
}) => {
  return (
    <StyledModal
      visible={visible}
      title='Barcode Mismatch Detected'
      onCancel={onCancel}
      maskClosable={false}
      width={620}
      footer={[
        <>
          <FormButton
            key='cancel'
            text='Cancel'
            type='secondary'
            onClick={onCancel}
            icon={null}
          />
          <FormButton
            key='overwrite'
            type='primary'
            onClick={onOverwrite}
            text='Use Detected Values'
            icon={null}
          />
        </>,
      ]}
    >
      <Space size={8} style={{ marginBottom: 8 }}>
        <WarningOutlined style={{ color: '#faad14', fontSize: 18 }} />
        <Typography.Text style={{ fontSize: 14 }}>
          The barcode does not match the identifiers you have entered. Scanning
          this barcode return
        </Typography.Text>
      </Space>
      {mismatches.map((mismatch, index) => (
        <Row
          style={{
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            marginBottom: 8,
            padding: 6,
          }}
          key={index}
          gutter={[4, 6]}
        >
          <Col span={24}>
            <Typography.Text strong style={{ fontSize: 14 }}>
              {mismatch.field}:
            </Typography.Text>
          </Col>
          <Col span={24}>
            <Typography.Text>Your value:</Typography.Text>
            <Typography.Text strong>
              <code style={{ fontSize: 14 }}>{mismatch.entered}</code>
            </Typography.Text>
            <br />
          </Col>
          <Col span={24}>
            <Typography.Text>Detected: </Typography.Text>
            <Typography.Text strong>
              <code style={{ fontSize: 14 }}>{mismatch.detected}</code>
            </Typography.Text>
          </Col>
        </Row>
      ))}
      <Typography.Text style={{ fontSize: 12, color: '#8c8c8c' }}>
        You can either keep your entered values or overwrite them with the
        detected barcode values.
      </Typography.Text>
    </StyledModal>
  );
};

const BarcodeDetectionStatus = ({ barcodes, children }) => {
  if (!barcodes || barcodes.length === 0) {
    return (
      <div style={{ marginTop: 8, padding: 8, backgroundColor: '#f5f5f5' }}>
        <Space size={4}>
          <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
          <Typography.Text style={{ fontSize: 14, color: '#faad14' }}>
            No barcode detected
          </Typography.Text>
        </Space>
      </div>
    );
  }

  const sortedBarcodes = sortBarcodesByFormat(barcodes);

  return (
    <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fafafa' }}>
      <div style={{ paddingBottom: 4 }}>
        <Typography.Text strong>Barcode detected</Typography.Text>
        {children}
      </div>
      {sortedBarcodes.map((barcode, index) => (
        <Row
          key={index}
          style={{ paddingLeft: 18, paddingBottom: 2 }}
          align='middle'
        >
          <Col flex='50px'>
            <Typography.Text>
              {BARCODE_FORMAT[barcode.barcodeFormat]?.toUpperCase() ||
                barcode.barcodeFormat}
              :
            </Typography.Text>
          </Col>
          <Col flex='120px'>
            <Typography.Text strong>
              <code style={{ fontSize: 14 }}>{barcode.barcodeText}</code>
            </Typography.Text>
          </Col>
          <Col flex='auto'>
            <CopyButton text={barcode.barcodeText} />
          </Col>
        </Row>
      ))}
    </div>
  );
};

const UploadBarcodeModal = ({ visible, onCancel, onFileUploaded }) => {
  const [selectedFile, setSelectedFile] = useState(undefined);

  const barcodeMutation = useReadBarcodeImage();

  const onFileChange = async (fileResult) => {
    if (fileResult?.file?.status?.includes('error')) {
      setSelectedFile(undefined);
      return;
    }

    if (fileResult?.file?.status.indexOf('uploading') >= 0) {
      const file = fileResult?.file?.originFileObj;
      setSelectedFile(file);
    } else {
      setSelectedFile(undefined);
    }
  };

  const onSubmitBarcode = () => {
    if (selectedFile) {
      barcodeMutation.mutate(selectedFile, {
        onSuccess: (response) => {
          if (response?.isSuccess && response?.data) {
            const barcodes = response?.data?.barcodes ?? [];
            onFileUploaded(barcodes);
            setSelectedFile(undefined);
          } else {
            CustomNotification.error(
              response?.message ?? 'Failed to upload barcode'
            );
          }
        },
        onError: (error) => {
          CustomNotification.error(
            error?.message ?? 'An error occurred while uploading'
          );
        },
      });
    }
  };

  const onModalCancel = () => {
    setSelectedFile(undefined);
    barcodeMutation.reset();
    onCancel();
  };

  return (
    <StyledModal
      title='Upload Barcode Image'
      visible={visible}
      onCancel={onModalCancel}
      maskClosable={false}
      destroyOnClose={false}
      width={620}
      footer={[
        <Button
          key='cancel'
          onClick={onModalCancel}
          disabled={barcodeMutation.isLoading}
        >
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={onSubmitBarcode}
          disabled={!selectedFile}
          loading={barcodeMutation.isLoading}
        >
          Submit
        </Button>,
      ]}
    >
      <UploadFile
        manualUpload={true}
        showMultiple={false}
        getFileChange={onFileChange}
        onlyAcceptImage={true}
        supportTypes={SUPPORT_IMAGES_TYPES.filter((item) => item !== 'pdf')}
        showSubmitBtn={false}
      />
    </StyledModal>
  );
};
